import { useParams, Link } from 'react-router-dom';
import { getArticleById, getRelatedArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleById(slug) : undefined;
  const category = article ? categories.find((c) => c.slug === article.category) : undefined;
  const relatedArticles = article ? getRelatedArticles(article.id, article.category, 3) : [];

  if (!article) {
    return (
      <>
        <ScrollToTop />
        <Navbar />
        <main className="pt-16 md:pt-[72px] min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-background-200/70"><i className="ri-error-warning-line text-2xl text-foreground-400"></i></div>
            <h1 className="text-xl font-heading font-semibold text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-4">该文章不存在或已被移除</p>
            <Link to="/" className="inline-block px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-medium hover:bg-primary-600 transition-colors">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const jsonLd = { '@context': 'https://schema.org', '@graph': [{ '@type': 'Article', headline: article.title, description: article.excerpt, author: { '@type': 'Person', name: article.author }, datePublished: article.date, image: article.heroImageUrl, publisher: { '@type': 'Organization', name: '庭院花园' }, url: `https://tingyuanhuayuan.cn/article/${article.id}` }, { '@type': 'BreadcrumbList', itemListElement: [{ '@type': 'ListItem', position: 1, name: '首页', item: 'https://tingyuanhuayuan.cn/' }, { '@type': 'ListItem', position: 2, name: category?.name || '', item: `https://tingyuanhuayuan.cn/category/${article.category}` }, { '@type': 'ListItem', position: 3, name: article.title, item: `https://tingyuanhuayuan.cn/article/${article.id}` }] }] };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      <Navbar />
      <main className="pt-16 md:pt-[72px]">
        <Breadcrumb />
        <article className="w-full px-4 md:px-6 py-8 md:py-12 bg-background-50">
          <div className="max-w-[800px] mx-auto">
            <header className="mb-8">
              <div className="flex items-center gap-2 mb-4">
                {category && (<Link to={`/category/${category.slug}`} className="text-xs text-accent-600 bg-accent-100 px-3 py-1 rounded-full font-medium hover:bg-accent-200 transition-colors cursor-pointer">{category.name}</Link>)}
                <span className="text-xs text-foreground-400">{article.readTime}</span>
              </div>
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight">{article.title}</h1>
              <div className="flex items-center gap-3 mt-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1"><i className="ri-user-line text-xs"></i> {article.author}</span>
                <span className="flex items-center gap-1"><i className="ri-calendar-line text-xs"></i> {article.date}</span>
              </div>
              <div className="flex items-center gap-2 mt-3 flex-wrap">
                {article.tags.map((tag) => (<span key={tag} className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-full border border-background-200/70">#{tag}</span>))}
              </div>
            </header>
            <div className="w-full h-64 md:h-[420px] rounded-xl overflow-hidden mb-8">
              <img src={article.heroImageUrl} alt={article.title} title={`${article.title} | 庭院花园设计`} className="w-full h-full object-cover object-top" />
            </div>
            <div className="prose-content text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article.content }} />
          </div>
        </article>
        {relatedArticles.length > 0 && (
          <section className="w-full px-4 md:px-6 py-12 md:py-16 bg-background-100">
            <div className="max-w-[900px] mx-auto">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">相关文章推荐</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                {relatedArticles.map((rel) => (
                  <Link key={rel.id} to={`/article/${rel.id}`} className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-200 cursor-pointer">
                    <div className="w-full h-44 overflow-hidden">
                      <img src={rel.heroImageUrl} alt={rel.title} title={rel.title} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                    </div>
                    <div className="p-4">
                      <span className="text-xs text-accent-600 bg-accent-100 px-2 py-0.5 rounded-full font-medium">{categories.find((c) => c.slug === rel.category)?.name}</span>
                      <h3 className="mt-2 text-sm font-heading font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2">{rel.title}</h3>
                      <p className="mt-1.5 text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>
      <Footer />
    </>
  );
}